import { put, select, takeEvery } from "redux-saga/effects";
import { v4 as uuidv4 } from "uuid";

import { ScanLabelAction } from "actions";
import {
  ApplyQueuedScanLabelsToEidsAction,
  setQueuedScanLabelNames,
} from "actions/queuedScanLabels";

import { APPLY_QUEUED_SCAN_LABELS_TO_EIDS } from "constants/actionTypes";
import { ScanLabelArchetype } from "constants/scanLabels";

import { getLivestockSaleId } from "lib/navigation";

import {
  getQueuedScanLabelNames,
  getScans,
  selectArchetypeByScanLabelName,
  selectScanLabelsByScanIdLookup,
} from "selectors";

interface NewScanLabelPayload {
  id: string;
  scanId: string;
  name: string;
  archetype: ScanLabelArchetype;
  livestockSaleId: number;
}

// Fires after "add scans offline" has dispatched, but its success action may
// not have landed yet, so `scan.id` below can still be an offline temp id.
// That is fine: ScanLabelAction.createBulk's payload rides on the existing
// offline temp-id rewrite machinery, which swaps the temp scanId for the
// real one once "add scans offline success" arrives, before this label
// create is sent.
function* onApplyQueuedScanLabelsToEids(
  action: ApplyQueuedScanLabelsToEidsAction,
) {
  const { eids } = action;

  const queuedNames: string[] = yield select(getQueuedScanLabelNames);
  if (queuedNames.length === 0) {
    return;
  }

  const scansByEid: Record<string, Scan> = yield select(getScans);
  const scanLabelsByScanId: Record<string, ScanLabel[]> = yield select(
    selectScanLabelsByScanIdLookup,
  );
  const archetypeByLabelName: Record<string, ScanLabelArchetype> = yield select(
    selectArchetypeByScanLabelName,
  );
  const livestockSaleId: number = getLivestockSaleId();

  const toCreate: NewScanLabelPayload[] = [];

  eids.forEach(eid => {
    const scan = scansByEid[eid];
    if (!scan?.id) {
      return;
    }

    const existingLabels = scanLabelsByScanId[scan.id] || [];

    queuedNames.forEach(name => {
      if (!existingLabels.some(label => label.name === name)) {
        toCreate.push({
          id: uuidv4(),
          scanId: scan.id,
          name,
          archetype: archetypeByLabelName[name] || ScanLabelArchetype.NONE,
          livestockSaleId,
        });
      }
    });
  });

  if (toCreate.length > 0) {
    yield put(ScanLabelAction.createBulk(toCreate));
  }

  yield put(setQueuedScanLabelNames([]));
}

export default function* queuedScanLabelsSaga() {
  yield takeEvery(
    APPLY_QUEUED_SCAN_LABELS_TO_EIDS,
    onApplyQueuedScanLabelsToEids,
  );
}
